import { PayloadAction } from '@reduxjs/toolkit';
import { call, delay, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { AIDuplicateTrainedModel, AIRunModel, CreateModel, ModelDependency, OnnxUploadSession, StructureColumnTypeEnum, TrainedModel } from '../../@shared/Models/Models';
import RespondClient from '../../@shared/Models/RespondClient';
import { TrainedModelProviderInstance } from '../../@shared/services/TrainedModelProvider';
import { setIsModelDuplicatedTo, setIsModelWasRemoved, setOpenedSchema } from '../../reducers/data/dataReducer';
import { GuideStepEnum, NotificationModel, NotificationModelType } from '../../reducers/main/mainModel';
import { cleanTrainedModel, getAiModelId, getAIModelTrainedModelId, getAIModelTrainerTypeId, getModelDependencies, getOnnxModalTrainerId, getRunData, getRunSchemaOrdering, getRunSchemaOrderingResult, getTrainedModelDescription, getTrainedModelName, getTrainedModelSettings, setAiModelId, setAIModelTrainedModelId, setAIModelTrainerTypeId, setDuplicateModelId, setIsLoadingResults, setIsModelUpdating, setIsOnnxUploading, setIsShowDuplicateModelModal, setIsShowOnnxModal, setIsShowRunTrainedModelModal, setIsShowTrainedModelModal, setModelDependencies, setOnnxModalTrainerId, setOnnxUploadProgress, setOpenedModel, setRunData, setRunResult, setRunSchemaOrdering, setRunSchemaOrderingResult, setTrainedModelDescription, setTrainedModelName, setTrainedModelSettings } from '../../reducers/trainedModel/trainedModelReducer';
import { onThrowAlert, loadOpenedModel, loadOpenedSchema } from '../dataSaga/dataSagaActions';
import { filterHierarchy, getDefaultValueForSchemaItem, getSchemaNamesDictionary } from '../../@shared/schemaUtils';

import { changeAIModelTrainerTypeId, closeDuplicateModelModalAction, closeOnnxModalAction, closeRunModelModalAction, closeTrainedModelModalAction, createDuplicateModelAction, editTrainedModelModalAction, openDuplicateModelModalAction, openOnnxModalAction, openRunModelModalAction, openTrainedModelModalAction, removeTrainedModelAction, runModelModalAction, saveTrainedModelModalAction, uploadOnnxModalAction } from './trainedModelSagaActions';
import { FormatJSONString } from '../../@shared/util';
import { ModelType } from '../../@shared/Models/ModelTypes';
import { getGuideStep, setGuideStep } from '../../reducers/main/mainReducer';

const initSaga = [
    takeLatest(openTrainedModelModalAction, watchOpening),
    takeLatest(editTrainedModelModalAction, watchEditing),
    takeLatest(closeTrainedModelModalAction, watchCloasing),
    takeLatest(saveTrainedModelModalAction, watchSaving),
    takeLatest(openRunModelModalAction, watchOpeningRun),
    takeLatest(closeRunModelModalAction, watchClosingRun),
    takeLatest(runModelModalAction, watchSavingRun),
    takeLatest(changeAIModelTrainerTypeId, watchChangingType),
    takeLatest(removeTrainedModelAction, watchRemoving),
    takeLatest(closeOnnxModalAction, watchCloasingOnnxModal),
    takeLatest(uploadOnnxModalAction, watchUploadingOnnx),
    takeLatest(openOnnxModalAction, watchOpenningOnnxModal),
    takeLatest(openDuplicateModelModalAction, watchDuplicating),
    takeLatest(closeDuplicateModelModalAction, watchClosingDuplicating),
    takeLatest(createDuplicateModelAction, watchCreateDuplicate),
]


export default initSaga;

function* watchOpening({ payload: data }: PayloadAction<string>): Generator {
    try {
        yield put(setIsShowTrainedModelModal(true));
        yield put(setModelDependencies([]));
        yield put(setAiModelId(data));
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

function* watchEditing({ payload: data }: PayloadAction<TrainedModel>): Generator {
    try {
        yield put(setIsShowTrainedModelModal(true));
        yield put(setTrainedModelSettings(FormatJSONString(data.advancedSettings)));
        yield put(setAIModelTrainerTypeId(data.modelTypeId));
        yield put(setTrainedModelName(data.name));
        yield put(setModelDependencies((data.modelDependencies ?? []).map(x => ({ schemaItemId: x.schemaItemId, refModelId: x.refModelId }))));
        yield put(setAiModelId(data.schemaId));
        yield put(setAIModelTrainedModelId(data.id));
        yield put(setTrainedModelDescription(data.description));
        yield put(setOpenedSchema(data.schema));
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

function* watchCloasing(): Generator {
    try {
        yield put(setIsShowTrainedModelModal(false));
        yield put(cleanTrainedModel());
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

function* watchSaving(): Generator {
    try {
        yield put(setIsModelUpdating(true));

        const name = (yield select(getTrainedModelName)) as string;
        const description = (yield select(getTrainedModelDescription)) as string;

        const settings = (yield select(getTrainedModelSettings)) as string;
        const settingsValue = !!settings ? JSON.parse(settings) : null;
        const AiModelId = (yield select(getAiModelId)) as string;
        const aiModelTrainerTypeId = (yield select(getAIModelTrainerTypeId)) as ModelType;
        const aiModelTrainedModelId = (yield select(getAIModelTrainedModelId)) as string;
        const modelDependencies = (yield select(getModelDependencies)) as ModelDependency[];
        const guideStep = (yield select(getGuideStep)) as GuideStepEnum;

        const data = {
            id: aiModelTrainedModelId,
            name: name,
            schemaId: AiModelId,
            modelTypeId: aiModelTrainerTypeId,
            settings: settingsValue,
            description: description,
            modelDependencies: modelDependencies
        } as CreateModel;
        const result = (yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.create], data)) as RespondClient<string>;;

        if (result.status !== 200) {
            yield put(onThrowAlert({
                message: result.data,
                lifeTime: 10000
            } as NotificationModel));
        }
        else {
            yield put(setIsShowTrainedModelModal(false));
            yield put(cleanTrainedModel());
            yield put(loadOpenedSchema(AiModelId));

            if (!!aiModelTrainedModelId) {
                yield put(loadOpenedModel(aiModelTrainedModelId));
            }

            yield put(onThrowAlert({
                message: "Model successfully saved",
                lifeTime: 3000,
                type: NotificationModelType.Success
            } as NotificationModel));

            if (guideStep !== GuideStepEnum.None) {
                yield put(setGuideStep(GuideStepEnum.OpenCreatedModel));
            }
        }
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
    finally {
        yield put(setIsModelUpdating(false));
    }
}

function* watchChangingType({ payload: data }: PayloadAction<ModelType>): Generator {
    try {
        yield put(setAIModelTrainerTypeId(data));
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

function* watchOpeningRun({ payload: data }: PayloadAction<TrainedModel>): Generator {
    try {
        yield put(setIsShowRunTrainedModelModal(true));
        yield put(setAiModelId(data.id));
        yield put(setOpenedModel(data));
        const filtered = filterHierarchy(data?.schema?.schemaItems, [StructureColumnTypeEnum.Feature]);

        const flattenedItems = getSchemaNamesDictionary(filtered)
            .slice()
            .sort((a, b) => a.key.localeCompare(b.key));

        const valueItem: unknown[] = [];
        flattenedItems
            .filter(x => x.schemaItem.typeId !== StructureColumnTypeEnum.Target)
            .forEach(x => {
                valueItem.push(getDefaultValueForSchemaItem(x.schemaItem));
            });

        const isUseDefault = flattenedItems.filter(x => x.schemaItem.typeId === StructureColumnTypeEnum.Target).length === 0;

        const baseOrder = flattenedItems.filter(x => x.schemaItem.typeId !== StructureColumnTypeEnum.Target).map(x => x.key).join(",")
        const baseResultOrder = flattenedItems.filter(x => x.schemaItem.typeId === StructureColumnTypeEnum.Target).map(x => x.key).join(",")
        yield put(setRunData(JSON.stringify([valueItem], null, 2)));
        yield put(setRunSchemaOrdering(baseOrder));
        yield put(setRunSchemaOrderingResult(isUseDefault ? "PredictedLabel" : baseResultOrder));
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

function* watchDuplicating({ payload: data }: PayloadAction<string>): Generator {
    try {
        yield put(setIsShowDuplicateModelModal(true));
        yield put(setDuplicateModelId(data));
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

function* watchCreateDuplicate({ payload: data }: PayloadAction<AIDuplicateTrainedModel>): Generator {
    try {
        const result = (yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.duplicate], data)) as RespondClient<TrainedModel>;;
        if (result.status !== 200) {
            yield put(onThrowAlert({
                message: result.data.toString(),
                lifeTime: 10000
            } as NotificationModel));
        }
        else {
            yield put(setIsShowDuplicateModelModal(false));
            yield put(setDuplicateModelId(""));
            yield put(cleanTrainedModel());
            yield put(setIsModelDuplicatedTo(result.data.name));
            yield put(onThrowAlert({
                message: "Model successfully duplicated",
                lifeTime: 3000,
                type: NotificationModelType.Success
            } as NotificationModel));
        }
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

function* watchClosingDuplicating(): Generator {
    try {
        yield put(setIsShowDuplicateModelModal(false));
        yield put(setDuplicateModelId(""));
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

function* watchClosingRun(): Generator {
    try {
        yield put(setIsShowRunTrainedModelModal(false));
        yield put(cleanTrainedModel());
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

function* watchSavingRun(): Generator {
    try {

        const AiModelId = (yield select(getAiModelId)) as string;
        const dataString = (yield select(getRunData)) as string;
        const schemaOrdering = (yield select(getRunSchemaOrdering)) as string;
        const schemaOrderingResult = (yield select(getRunSchemaOrderingResult)) as string;
        yield put(setIsLoadingResults(true));

        const data = {
            data: JSON.parse(dataString),
            schemaOrdering: schemaOrdering.split(",").map(x => x.trim()),
            resultSchemaOrdering: schemaOrderingResult.split(",").map(x => x.trim())
        } as AIRunModel;
        const result = (yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.run], AiModelId, data)) as RespondClient<AIRunModel>;
        if (result.status !== 200) {
            if (typeof result.data === "string") {
                yield put(onThrowAlert({
                    message: result.data as string,
                    lifeTime: 10000
                } as NotificationModel));
            }
            else {
                yield put(onThrowAlert({
                    message: JSON.stringify(result.data),
                    lifeTime: 10000
                } as NotificationModel));
            }
        }
        else {
            yield put(setRunResult(result.data));
        }
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
    finally {
        yield put(setIsLoadingResults(false));
    }
}

function* watchRemoving({ payload: data }: PayloadAction<string>): Generator {
    try {
        const result = (yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.remove], data)) as RespondClient<string>;
        if (result.status !== 200) {
            yield put(onThrowAlert({
                message: result.data,
                lifeTime: 10000
            } as NotificationModel));
        }
        else {
            yield put(setIsModelWasRemoved(true));
            yield put(onThrowAlert({
                message: "Model successfully removed",
                lifeTime: 3000,
                type: NotificationModelType.Warning
            } as NotificationModel));
        }

    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

function* watchOpenningOnnxModal({ payload: data }: PayloadAction<string>): Generator {
    try {
        yield put(setOnnxModalTrainerId(data));
        yield put(setIsShowOnnxModal(true));
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

function* watchCloasingOnnxModal(): Generator {
    try {
        yield put(setIsShowOnnxModal(false));

    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

const ONNX_CHUNK_RETRIES = 3;

function* uploadOnnxChunk(id: string, uploadId: string, index: number, chunk: Blob): Generator {
    for (let attempt = 1; ; attempt++) {
        let result: RespondClient<string> | undefined;
        try {
            result = (yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.uploadOnnxChunk], id, uploadId, index, chunk)) as RespondClient<string>;
        }
        catch {
            // Network error, worth retrying
        }

        if (result?.status === 200) {
            return;
        }

        // 4xx means the request itself is wrong, a retry won't help
        const isRetryable = !result || result.status >= 500 || result.status === 429;
        if (!isRetryable || attempt >= ONNX_CHUNK_RETRIES) {
            throw new Error(result?.data || `Failed to upload chunk ${index + 1}`);
        }

        yield delay(1000 * attempt);
    }
}

function* uploadOnnxFile(id: string, file: File, session: OnnxUploadSession): Generator {
    for (let index = 0; index < session.chunkCount; index++) {
        const chunk = file.slice(index * session.chunkSize, (index + 1) * session.chunkSize);
        yield call(uploadOnnxChunk, id, session.uploadId, index, chunk);
        yield put(setOnnxUploadProgress(Math.round((index + 1) / session.chunkCount * 100)));
    }

    const result = (yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.completeOnnxUpload], id, session.uploadId)) as RespondClient<string>;
    if (result.status !== 200) {
        throw new Error(result.data);
    }
}

function* watchUploadingOnnx({ payload: file }: PayloadAction<File>): Generator {
    const id = (yield select(getOnnxModalTrainerId)) as string;
    let session: OnnxUploadSession | undefined;

    try {
        yield put(setOnnxUploadProgress(0));
        yield put(setIsOnnxUploading(true));

        const started = (yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.startOnnxUpload], id, { totalSize: file.size })) as RespondClient<OnnxUploadSession | string>;
        if (started.status !== 200) {
            throw new Error(started.data as string);
        }
        const uploadSession = started.data as OnnxUploadSession;
        session = uploadSession;

        const { cancelled } = (yield race({
            uploaded: call(uploadOnnxFile, id, file, uploadSession),
            cancelled: take(closeOnnxModalAction),
        })) as { cancelled?: unknown };

        if (cancelled) {
            yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.abortOnnxUpload], id, uploadSession.uploadId);
            return;
        }

        yield put(setIsShowOnnxModal(false));
        yield put(loadOpenedModel(id));
        yield put(onThrowAlert({
            message: "Model successfully uploaded",
            lifeTime: 3000,
            type: NotificationModelType.Success
        } as NotificationModel));
    }
    catch (error) {
        if (session) {
            try {
                yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.abortOnnxUpload], id, session.uploadId);
            }
            catch {
                // Abandoned uploads are removed by the cleanup job
            }
        }
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
    finally {
        yield put(setIsOnnxUploading(false));
        yield put(setOnnxUploadProgress(0));
    }
}